import React, { useRef } from "react";
import { Paper, Box, Flex, Tooltip } from "@mantine/core";
import useColors from "../../../theme/useColors";
import { gray } from "../../../constants/color-constants";

const COLORS = {
  LABEL: gray[6],
  VALUE: gray[8],
};

const labelStyle: React.CSSProperties = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "12px",
  lineHeight: "20px",
  fontWeight: 500,
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const valueStyle: React.CSSProperties = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "12px",
  lineHeight: "18px",
  fontWeight: 500,
};

interface InfoPanelItemProps {
  label: string;
  value: string | React.ReactNode;
  style?: React.CSSProperties;
  breakOnSeparator?: boolean;
}

const InfoPanelItem: React.FC<InfoPanelItemProps> = ({
  label,
  value,
  style,
  breakOnSeparator = false,
}) => {
  const isStringValue = typeof value === "string";
  const shouldShowTooltip = isStringValue && value.length > 50;
  const visibleText = shouldShowTooltip ? value.slice(0, 50) + "…" : value;

  const renderValue = () => {
    if (!isStringValue) {
      return <Box style={{ ...valueStyle, color: COLORS.VALUE }}>{value}</Box>;
    }

    if (breakOnSeparator) {
      return (
        <Box style={{ ...valueStyle, color: COLORS.VALUE }}>
          {value.split("$").map((part, index) => (
            <Box component='span' display='block' key={index}>
              {part.trim()}
            </Box>
          ))}
        </Box>
      );
    }

    if (shouldShowTooltip) {
      return (
        <Tooltip label={value} position='bottom' withArrow multiline>
          <Box
            component='span'
            display='block'
            style={{
              ...valueStyle,
              color: COLORS.VALUE,
              overflow: "hidden",
            }}
          >
            {visibleText}
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box
        component='span'
        display='block'
        style={{
          ...valueStyle,
          color: COLORS.VALUE,
          whiteSpace: "pre-line",
        }}
      >
        {visibleText}
      </Box>
    );
  };

  return (
    <Box
      style={{
        minWidth: "80px",
        maxWidth: "320px",
        width: "fit-content",
        flexShrink: 0,
        flexGrow: 1,
        minHeight: "fit-content",
        ...style,
      }}
    >
      <Box style={{ ...labelStyle, color: COLORS.LABEL }}>{label}</Box>
      {renderValue()}
    </Box>
  );
};

interface InfoPanelProps {
  data: Record<string, string | React.ReactNode>;
  multiLineKeys?: string[];
  className?: string;
}

export const InfoPanel: React.FC<InfoPanelProps> = ({
  data,
  multiLineKeys = [],
  className,
}) => {
  const entries = Object.entries(data);
  const colors = useColors();
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Paper
      style={{
        border: `1px solid ${colors.gray[1]}`,
        width: "100%",
        flexShrink: 0,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        scrollbarColor: `${colors.primary[5]} transparent`,
      }}
      pt='4px'
      pb='4px'
      withBorder
      bg={colors.gray[2]}
      w='100%'
      className={className}
    >
      <Box>
        <div ref={containerRef} style={{ width: "100%" }}>
          <Flex
            style={{
              width: "fit-content",
              minWidth: "100%",
              justifyContent: "space-between",
              gap: 0,
            }}
            align='flex-start'
            wrap='nowrap'
          >
            {entries.map(([key, value], index) => (
              <InfoPanelItem
                key={key}
                label={key}
                value={value}
                breakOnSeparator={multiLineKeys.includes(key)}
                style={{
                  paddingLeft: index === 0 ? "16px" : "8px",
                  paddingRight: index === entries.length - 1 ? "16px" : "8px",
                }}
              />
            ))}
          </Flex>
        </div>
      </Box>
    </Paper>
  );
};
